// `configureAdsData` — the injected-config seam. Packages are variant-agnostic
// and store-free: the app injects its reactive auth-token hook, its shared
// QueryClient and its notifications query hook at bootstrap
// (`src/composition/hosts/ads/ads-data-wiring.ts`). Transport is NOT part of this seam —
// `data/hcp-verification` reads the app's configured clients from the
// `@heidi/api/client` registry. Mirrors the commerce and workspace seams.

import type { UserResponse } from '@heidi/workspace/contracts';
import { type QueryClient } from '@tanstack/react-query';

/**
 * The notifications row as the inmail sync reads it — every field optional so
 * the app's `UserInmailResponse` rows assign without a cast.
 */
export interface AdsDataInmail {
  id?: string | null;
  notification_name?: string | null;
  title?: string | null;
  created_at?: string | null;
}

/** The injected app config for `@heidi/ads/data`. */
export interface AdsDataConfig {
  /**
   * The reactive auth token, injected as a HOOK so gated queries re-evaluate
   * on token refresh instead of freezing the first-render value.
   */
  useAuthToken: () => string | null | undefined;
  /**
   * The app's shared QueryClient, so module-scope cache commands
   * (`invalidateQueries`) hit the SAME cache the app renders from.
   */
  queryClient: QueryClient;
  /**
   * The app's notifications query hook, by reference — the inmail sync only
   * reads `data`, so the minimal result shape is all this type names.
   */
  useQueryNotifications: () => { data?: readonly AdsDataInmail[] };
  /**
   * The app's `useQueryUser` hook, injected whole — a package data layer may
   * not import a foreign domain's /data door, so the user gate crosses here.
   * Only `data` is read, so the minimal result shape is all this type names.
   */
  useQueryUser: <T>(select?: (user: UserResponse | undefined) => T) => { data?: T };
}

let config: AdsDataConfig | null = null;

export const configureAdsData = (next: AdsDataConfig): void => {
  config = next;
};

/** Fails loudly rather than silently degrading — the workspace/commerce rule. */
export const getAdsDataConfig = (): AdsDataConfig => {
  if (!config) {
    throw new Error(
      '[ads/data] not configured — see src/composition/hosts/ads/ads-data-wiring.ts'
    );
  }
  return config;
};

export const resetAdsDataConfigForTests = (): void => {
  config = null;
};
